import React, { useEffect, useState } from "react";
import { Form, Container, Card } from "react-bootstrap";
import { addDayPlan, editDayPlan } from "../../store/slices/package";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";

export default function DayPlanStep({
  packageId,
  durationDays,
  days,
  setDays,
  prevStep,
  nextStep,
}) {
  const dispatch = useDispatch();

  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (!durationDays) return;

    const totalDays = Number(durationDays);

    setDays((prevDays) => {
      const updated = Array.from({ length: totalDays }, (_, index) => ({
        _id: prevDays[index]?._id || null,
        dayNumber: index + 1,
        title: prevDays[index]?.title || "",
        description: prevDays[index]?.description || "",
      }));

      return updated;
    });
  }, [durationDays]);

  const handleChange = (index, field, value) => {
    const updatedDays = [...days];
    updatedDays[index] = { ...updatedDays[index], [field]: value };
    setDays(updatedDays);

    const updatedErrors = [...errors];
    if (!updatedErrors[index]) updatedErrors[index] = {};

    if (field === "title") {
      if (!value || value.trim() === "") {
        updatedErrors[index].title = "Please enter title";
      } else {
        updatedErrors[index].title = "";
      }
    }

    if (field === "description") {
      if (!value || value.trim() === "") {
        updatedErrors[index].description = "Please enter description";
      } else {
        updatedErrors[index].description = "";
      }
    }

    setErrors(updatedErrors);
  };

  const validateDays = () => {
    let isValid = true;
    const newErrors = [];

    days.forEach((day, index) => {
      newErrors[index] = {};

      if (!day.title || day.title.trim() === "") {
        newErrors[index].title = "Please enter title";
        isValid = false;
      }

      if (!day.description || day.description.trim() === "") {
        newErrors[index].description = "Please enter description";
        isValid = false;
      }
    });

    setErrors(newErrors);
    return isValid;
  };

  const handleFinish = async () => {
    if (!validateDays()) return;

    if (!packageId) {
      toast.error("Package ID missing");
      return;
    }

    try {
      for (const day of days) {
        const payload = {
          packageId,
          dayNumber: day.dayNumber,
          title: day.title,
          description: day.description,
        };

        if (day._id) {
          //  UPDATE EXISTING DAY
          await dispatch(
            editDayPlan({
              id: day._id,
              ...payload,
            }),
          ).unwrap();
        } else {
          //  CREATE NEW DAY
          const res = await dispatch(addDayPlan(payload)).unwrap();

          day._id = res?._id || res?.data?._id || res?.day?._id || null;
        }
      }

      toast.success("Day Plans Saved Successfully");
      nextStep();
    } catch (error) {
      toast.error("Failed to save day plans");
    }
  };

  return (
    <Container className="mt-2">
      <Card className="p-4 shadow-sm border-0 mt-2">
        {days.map((day, index) => (
          <Card key={index} className="p-3 mb-3 shadow-sm">
            <h6>Day {day.dayNumber}</h6>

            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>
              <Form.Control
                value={day.title}
                placeholder={`Enter title for Day ${day.dayNumber}`}
                onChange={(e) => handleChange(index, "title", e.target.value)}
                maxLength={300}
                minLength={3}
              />
              {errors[index]?.title && (
                <small style={{ color: "red" }}>{errors[index].title}</small>
              )}
            </Form.Group>

            <Form.Group>
              <Form.Label>Description</Form.Label>
              <CKEditor
                editor={ClassicEditor}
                data={day.description}
                config={{
                  toolbar: [
                    "undo",
                    "redo",
                    "|",
                    "heading",
                    "|",
                    "bold",
                    "italic",
                    "bulletedList",
                    "numberedList",
                    "|",
                    "indent",
                    "outdent",
                    "blockQuote",
                  ],
                }}
                onChange={(event, editor) => {
                  const data = editor.getData();
                  handleChange(index, "description", data);
                }}
              />
              {errors[index]?.description && (
                <small style={{ color: "red" }}>
                  {errors[index].description}
                </small>
              )}
            </Form.Group>
          </Card>
        ))}

        <div className="d-flex justify-content-between mt-4 mb-5">
          <button className="cancel-btn" onClick={prevStep}>
            Previous
          </button>

          <button className="submit-btn" onClick={handleFinish}>
            Next
          </button>
        </div>
      </Card>
    </Container>
  );
}